import { folderLabel } from "@/domains/documents/utils/briefFolder";
import React, { useMemo } from "react";
import { FolderArchive } from "lucide-react";
import type { TreeFolder } from "../../../types";
import { BreadcrumbItem } from "../../molecules/FolderTree/BreadcrumbItem";
import { BreadcrumbSeparator } from "../../atoms/BreadcrumbSeparator";
import { FolderIcon } from "../../atoms/FolderIcon";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useFolderDragDrop } from "../../../hooks/useFolderDragDrop";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { isEligibleMoveDropTarget, type MoveDragSelection } from "../../../utils/documentMoveDrag";
import type { UploadSelection } from "@/domains/documents/utils/uploadSelection";

/** Segments beyond this collapse their middle behind an ellipsis. */
const DEFAULT_MAX_VISIBLE_SEGMENTS = 4;

/**
 * Recursively find a folder by ID in the tree
 */
const findFolderById = (folders: TreeFolder[], id: string): TreeFolder | null => {
  for (const folder of folders) {
    if (folder.id === id) return folder;
    const found = findFolderById(folder.children, id);
    if (found) return found;
  }
  return null;
};

interface FolderBreadcrumbProps {
  /** Current selected folder (null = root) */
  currentFolder: TreeFolder | null;
  /** All folders for path building */
  folders: TreeFolder[];
  /** Called when breadcrumb item is clicked */
  onNavigate: (folderId: string | null) => void;
  /** Called when a document is dropped */
  onDropDocument?: (docId: string, folderId: string | null) => void;
  /** Called when a folder is moved */
  onMoveFolder?: (folderId: string, targetFolderId: string | null) => void;
  /** Called when multiple items are dropped */
  onDropSelection?: (docIds: string[], folderIds: string[], targetFolderId: string | null) => void;
  /** Called when OS files are dropped on a segment (routes the upload to that folder) */
  onDropFiles?: (selection: UploadSelection, targetFolderId: string | null) => void;
  /** Active table drag; used to expose valid breadcrumb destinations. */
  activeMove?: MoveDragSelection | null;
  /**
   * Renders a separator before the first segment too, so the trail reads as a
   * continuation of a label the host shows above it (the project name over the
   * folder tree) instead of starting flush.
   */
  leadingSeparator?: boolean;
  /** Whether to render navigation semantics on the wrapper (disable when already wrapped in a nav) */
  withNavigationRole?: boolean;
  /** Max segments rendered (clamped to ≥3); longer paths collapse the middle behind an ellipsis menu */
  maxVisibleSegments?: number;
  /**
   * Container files (.zip/.asice) the user has drilled into, outermost first.
   * Appended after the folder path so the whole location stays ONE trail rather
   * than a second row stacked under this one.
   */
  containerSegments?: Array<{ id: string; name: string }>;
  /**
   * Called when a container segment is clicked, with the number of container
   * levels to keep (index + 1); 0 means "back out to the folder itself".
   */
  onNavigateContainer?: (depth: number) => void;
}

/**
 * FolderBreadcrumb - Organism component
 *
 * Complete breadcrumb navigation with drag/drop support.
 * Uses BreadcrumbItem molecule for each breadcrumb segment.
 * Integrates useFolderDragDrop hook for centralized drag/drop logic.
 * Builds breadcrumb path from current folder to root.
 * Part of the atomic design system for folder tree display.
 *
 * @example
 * ```tsx
 * <FolderBreadcrumb
 *   currentFolder={currentFolder}
 *   folders={allFolders}
 *   onNavigate={handleNavigate}
 *   onDropDocument={handleDropDocument}
 *   onMoveFolder={handleMoveFolder}
 * />
 * ```
 */
export const FolderBreadcrumb = React.memo(function FolderBreadcrumb({
  currentFolder,
  folders,
  onNavigate,
  onDropDocument,
  onMoveFolder,
  onDropSelection,
  onDropFiles,
  activeMove = null,
  leadingSeparator = false,
  withNavigationRole = true,
  maxVisibleSegments = DEFAULT_MAX_VISIBLE_SEGMENTS,
  containerSegments = [],
  onNavigateContainer,
}: FolderBreadcrumbProps) {
  const { t } = useLanguage();
  // Initialize drag/drop hook
  const { isDraggedOver, handleDragEnter, handleDragLeave, handleDrop, handleDragOver } =
    useFolderDragDrop(
      folders,
      {
        onDropDocument,
        onMoveFolder,
        onDropSelection,
        onDropFiles,
      },
      false, // autoExpandEnabled = false for breadcrumbs
      activeMove?.folderIds ?? []
    );

  /**
   * Build breadcrumb path from current folder to root
   */
  const breadcrumbPath = useMemo(() => {
    if (!currentFolder) return [];

    const path: TreeFolder[] = [currentFolder];
    let current = currentFolder;

    while (current.parentId) {
      const parent = findFolderById(folders, current.parentId);
      if (parent) {
        path.unshift(parent);
        current = parent;
      } else {
        break;
      }
    }

    return path;
  }, [currentFolder, folders]);

  // Deep paths keep the top-level folder plus the last (maxVisibleSegments - 1)
  // levels; the middle collapses into a menu so a long trail can't overflow the
  // row. Values below 3 are clamped so the leading/trailing slices stay disjoint.
  const clampedMax = Math.max(maxVisibleSegments, 3);
  const isCollapsed = breadcrumbPath.length > clampedMax;

  // Empty state (at project root, outside every container) — nothing to
  // navigate back to yet.
  if (breadcrumbPath.length === 0 && containerSegments.length === 0) {
    return null;
  }

  const leadingSegments = isCollapsed ? breadcrumbPath.slice(0, 1) : breadcrumbPath;
  const collapsedSegments = isCollapsed ? breadcrumbPath.slice(1, -(clampedMax - 1)) : [];
  const trailingSegments = isCollapsed ? breadcrumbPath.slice(-(clampedMax - 1)) : [];

  const navigationProps = withNavigationRole
    ? { role: "navigation" as const, "aria-label": "Folder breadcrumb" }
    : {};

  const renderSegment = (folder: TreeFolder, showSeparator: boolean) => (
    <BreadcrumbItem
      key={folder.id}
      id={folder.id}
      name={folderLabel(folder)}
      icon={
        folder.variant ? (
          <FolderIcon isOpen={false} variant={folder.variant} className="size-3.5" />
        ) : undefined
      }
      showSeparator={showSeparator}
      isDragOver={isDraggedOver(folder.id)}
      isDropAvailable={
        activeMove !== null && isEligibleMoveDropTarget(folders, folder.id, activeMove.folderIds)
      }
      onNavigate={() => onNavigate(folder.id)}
      onDragOver={handleDragOver(folder.id)}
      onDragEnter={handleDragEnter(folder.id, false)}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop(folder.id)}
    />
  );

  return (
    <div className="flex min-w-0 items-center px-1 py-2 text-sm" {...navigationProps}>
      {leadingSegments.map((segment, index) =>
        renderSegment(segment, leadingSeparator || index > 0)
      )}
      {isCollapsed && (
        <div className="flex flex-shrink-0 items-center">
          <BreadcrumbSeparator />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="rounded px-1.5 font-medium text-slate-400 hover:text-slate-600"
                aria-label={t.projects.folder.showHiddenFolders.replace(
                  "{folders}",
                  // function replacement: folder names containing `$&` etc.
                  // must substitute literally
                  () => collapsedSegments.map(folderLabel).join(", ")
                )}
                title={collapsedSegments.map(folderLabel).join(" / ")}
              >
                …
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {collapsedSegments.map((segment) => (
                <DropdownMenuItem key={segment.id} onSelect={() => onNavigate(segment.id)}>
                  {folderLabel(segment)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
      {trailingSegments.map((segment) => renderSegment(segment, true))}
      {/* Containers continue the same trail: `Folder > Sub > archive.asice`.
          Their depth is the index + 1, so clicking one keeps that many levels
          open; the folder segments before them exit every container. */}
      {containerSegments.map((container, index) => (
        <BreadcrumbItem
          key={container.id}
          id={container.id}
          name={container.name}
          showSeparator={leadingSeparator || breadcrumbPath.length > 0 || index > 0}
          icon={<FolderArchive className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />}
          onNavigate={() => onNavigateContainer?.(index + 1)}
        />
      ))}
    </div>
  );
});
